import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { StatTile } from '@/components/StatTile'
import { PageHeader } from '@/app/layouts/PageHeader'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { Link } from 'react-router'
import { Media } from '@/components/Media'
import { formatMoney, fromMajor } from '@/lib/money'
import { CITIES, FLIGHTS, STAYS } from './travel-data'

/* Derived from the same inventory the hotel list filters, so a city can never
   advertise a price or a count the list then contradicts. */
const DESTINATIONS = CITIES.map((city) => {
  const stays = STAYS.filter((stay) => stay.city === city.id)
  const flight = FLIGHTS.find((item) => item.destination === city.id)
  const cheapest = Math.min(...stays.map((stay) => stay.nightly))
  return {
    id: city.id,
    city: city.label,
    country: city.country,
    stays: stays.length,
    from: fromMajor(cheapest, 'USD'),
    flightFrom: flight?.price ?? '—',
    image: stays[0]?.image ?? '',
    score: (stays.reduce((sum, stay) => sum + stay.score, 0) / (stays.length || 1)).toFixed(1),
  }
})

const WHY = [
  { icon: 'redeem', title: '3% back on stays', body: 'Paid after check-out' },
  { icon: 'flight', title: '1% back on flights', body: 'Paid on departure' },
  { icon: 'currency_exchange', title: '0% FX fee', body: 'Charged in USD' },
  { icon: 'event_available', title: 'Free cancellation', body: 'On most stays' },
]

export function TravelScreen() {
  return (
    <>
      <PageHeader
        title="Travel"
        subtitle="The trip home, priced in the currency you hold. 3% on stays, 1% on flights."
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="sim_card" to="/esim">eSIM</Button>
            <Button variant="primary" size="md" iconStart="luggage" to="/transactions">My trips</Button>
          </>
        }
      />

      <BentoGrid>
        <BentoSpan span={3}>
          <Card tone="dark">
            <Pill tone="accent">Members</Pill>
            <Text as="p" variant="heading" weight="bold" tone="onDark" className="pb-mt-4">
              3% on stays · 1% on flights · 5% on wellness
            </Text>
            <Text variant="caption" tone="onDarkMuted" className="pb-mt-2">
              0.9% FX spread, charged straight to your USD account. No booking fee on top.
            </Text>
            <Card.Footer>
              <Stack direction="row" gap={2} wrap>
                <Button size="md" iconStart="hotel" to="/travel/hotels">Hotels</Button>
                <Button variant="onDark" size="md" iconStart="flight" to="/travel/flights">Flights</Button>
                <Button variant="onDark" size="md" iconStart="spa" to="/wellness">Wellness</Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <Stack gap={3}>
          <StatTile stat={{ label: 'Travel spend 2026', value: '$4,218.40', note: '$92.80 earned back so far' }} />
          <Card tone="light">
            <Text variant="caption" weight="semibold">Upcoming</Text>
            <Stack gap={3} className="pb-mt-3">
              {[
                ['Hotel Mirasol', 'Mexico City · 12–15 Oct'],
                ['JFK → MEX', '12 Oct · 08:15 · Aeroméxico AM 405'],
              ].map(([title, detail]) => (
                <Stack key={title} gap={0}>
                  <Text variant="caption" weight="semibold" truncate>{title}</Text>
                  <Text variant="caption" tone="secondary" truncate>{detail}</Text>
                </Stack>
              ))}
            </Stack>
          </Card>
        </Stack>

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">Where members go</Text>
              <Text variant="eyebrow" tone="secondary">Pick a city to search</Text>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {DESTINATIONS.map((item) => (
                <Link
                  key={item.id}
                  to={`/travel/hotels?city=${item.id}`}
                  className={checkout.line}
                  style={{ width: '100%' }}
                >
                  <Media src={item.image} alt="" width={48} ratio={1} radius="md" icon="apartment" />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">{item.city}</Text>
                    <Text variant="caption" tone="secondary" truncate>
                      {item.stays} {item.stays === 1 ? 'stay' : 'stays'} · flights from {item.flightFrom}
                    </Text>
                  </Stack>
                  <Pill tone="neutral">{item.score}</Pill>
                  <Stack align="end" gap={0} style={{ width: '5rem' }}>
                    <Text variant="caption" weight="semibold" numeric>{formatMoney(item.from)}</Text>
                    <Text variant="eyebrow" tone="secondary">a night</Text>
                  </Stack>
                </Link>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">Why book here</Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {WHY.map((item) => (
                <div key={item.title} className={checkout.reason}>
                  <Icon name={item.icon} size={20} />
                  <Stack gap={1}>
                    <Text variant="caption" weight="semibold">{item.title}</Text>
                    <Text variant="eyebrow" tone="secondary">{item.body}</Text>
                  </Stack>
                </div>
              ))}
            </div>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
